import React, { MouseEventHandler, useEffect, useRef, useState } from 'react';

import { DrawerProps } from '@components/Drawer/next';
import ListingSearchSelect from '@components/Select/Select';
import { useOutsideClick } from '@hooks/hooks';
import { LocationIcon, CloseIcon } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { SearchViewModel } from '@api/v3';
import { getLocationIcon } from '@components/ui/search/SearchLocation/utils/getLocationIcon';
import { BookerSearchViewModel } from '@components/views/landing/components/booker/common/BookerSearchViewModel';
import { PastSearchesHeader } from '@components/views/landing/components/booker/common/PastSearchesHeader';
import { ClearSearchButton } from '@components/views/landing/components/booker/common/styled';
import { SearchResultError } from '@components/ui/search/SearchBarInput/SearchBarSearchInput/SearchBarSearchInputStyle';
import { SEARCH_LENGTH } from '@utils/utils';
import PopularSearches from '@components/views/landing/components/booker/common/PopularSearches';

import {
  ResponsiveListingSearchLocationSelect,
  ResponsiveLocationDrawerContent,
  ResponsiveLocationListItem,
  ResponsiveLocationListItems,
  StyledListingLocationDrawer,
  ResponsiveLocationListItemIcon,
  ResponsiveLocationListItemText,
  ResponsiveSearchDrawerButtonWrapper,
  ResponsiveSearchDrawerButton,
  ResponsiveLocationItems,
  ListItemsWrappedByHeader,
  ResponsiveLocationListItemContent,
} from './ResponsiveListingSearchStyle';
import ResponsiveSearchDrawerItem from './ResponsiveSearchDrawerItem';
type CustomHeaderType =
  | React.Component<{}, {}, any>
  | React.ReactElement<any, any>;

interface LocationDrawerInterface {
  showLocationModal: () => void;

  locationInput: string;
  handleLocationInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef?: any;

  locationInputError: boolean;
  handleCloseLocationSearch: () => void;
  handleUpdateButton: () => void;
  autoCompleteData: any;
  autoCompleteItemClicked: (i: any) => void;
  openAutoCompleteResultModal: boolean;
  setOpenAutoCompleteResultModal: React.Dispatch<React.SetStateAction<boolean>>;
  pastSearchesData?: any;
  locationDrawerSubTitle: string;
  headerComponent?: CustomHeaderType;
  onClearClick?: MouseEventHandler<HTMLButtonElement>;
  callBack?: (item: BookerSearchViewModel) => void;
  onRemoveItem?: (item: BookerSearchViewModel) => void;
  error?: boolean;
  isFilled?: boolean;
  type?: string;
  searchTextError?: boolean;
  searchTextLoading?: boolean;
  popularSearchItems?: SearchViewModel[];
  onPopularSearchItemSelected?: (item: SearchViewModel) => void;
}

const ResponsiveListingSearchLocation = (
  props: React.PropsWithChildren<
    React.PropsWithChildren<DrawerProps & LocationDrawerInterface>
  >
) => {
  const {
    showLocationModal,
    locationInput,
    type,
    handleLocationInputChange,
    autoCompleteItemClicked,
    autoCompleteData,
    inputRef,
    handleUpdateButton,
    handleCloseLocationSearch,
    headerComponent,
    setOpenAutoCompleteResultModal,
    locationDrawerSubTitle,
    pastSearchesData,
    onClearClick,
    callBack,
    error,
    onRemoveItem,
    isFilled,
    searchTextError,
    searchTextLoading,
    visible,
    popularSearchItems,
    onPopularSearchItemSelected,
    ...otherProps
  } = props;
  const [selectBox, setSelectedBox] = useState(false);
  const [filterResult, setFilterResult] = useState([]);
  const wrapperRef = useRef<any>(null);
  const [focused, setFocused] = useState<boolean>(false);
  useEffect(() => {
    setSelectedBox(true);
  }, []);

  useOutsideClick(wrapperRef, (): void => {
    setSelectedBox(false);
    setFocused(false);
  });

  // Update filterResult whenever autoCompleteData changes, regardless of focus state
  useEffect(() => {
    if (autoCompleteData && autoCompleteData.length > 0) {
      setOpenAutoCompleteResultModal(true);
      setFilterResult(autoCompleteData);
    }
  }, [autoCompleteData, setOpenAutoCompleteResultModal]);
  const autoCompleteListItemClick = (i: any) => {
    setOpenAutoCompleteResultModal(false);
    autoCompleteItemClicked(i);
    setFilterResult([]);
  };

  const getListItemsWrappedByHeader = (
    data: any,
    header: CustomHeaderType,
    historyData?: boolean
  ) => (
    <ListItemsWrappedByHeader>
      <>
        {header}
        {getListItems(data, historyData)}
      </>
    </ListItemsWrappedByHeader>
  );

  const getListItems = (data: any, historyData?: boolean) => {
    const handleItemClick = (item: SearchViewModel) => {
      autoCompleteListItemClick(item);
      setFocused(false);
      historyData && callBack?.(item);
    };
    return searchTextLoading ? null : searchTextError ? (
      <SearchResultError>
        Aradığınız kriterlere uygun sonuç bulunamadı.
      </SearchResultError>
    ) : (
      <ResponsiveLocationListItems>
        {data?.map((i: SearchViewModel) => (
          <ResponsiveLocationListItem key={i.id}>
            <ResponsiveLocationListItemContent
              onClick={() => handleItemClick(i)}
            >
              <ResponsiveLocationListItemIcon>
                {i.type ? (
                  <CustomIcon
                    paletteColor='matterHorn'
                    icon={getLocationIcon(i.type)}
                    fontSize={28}
                  />
                ) : null}
              </ResponsiveLocationListItemIcon>
              <ResponsiveLocationListItemText>
                {i?.text}
              </ResponsiveLocationListItemText>
            </ResponsiveLocationListItemContent>

            {historyData ? (
              <ClearSearchButton
                onClick={onRemoveItem ? () => onRemoveItem(i) : undefined}
              >
                <CustomIcon
                  paletteColor='matterHorn'
                  icon={CloseIcon}
                  fontSize={16}
                />
              </ClearSearchButton>
            ) : null}
          </ResponsiveLocationListItem>
        ))}
      </ResponsiveLocationListItems>
    );
  };

  const handleCloseModal = () => {
    setFocused(false);
    setFilterResult([]); // Clear results when modal is closed
    handleCloseLocationSearch();
  };

  const showPastSearch =
    pastSearchesData?.length > 0 && focused && locationInput.length === 0;

  const showFilteredItems =
    locationInput.length >= SEARCH_LENGTH &&
    (focused || filterResult.length > 0);

  const showPopularSearch =
    popularSearchItems &&
    popularSearchItems?.length > 0 &&
    locationInput.length === 0 &&
    focused;
  return (
    <>
      <ResponsiveSearchDrawerItem
        title='Nereye gideceksiniz?'
        subTitle={locationDrawerSubTitle}
        icon={LocationIcon}
        arrowIcon={false}
        onClick={showLocationModal}
        error={error}
        isFilled={isFilled}
      />
      {visible ? (
        <StyledListingLocationDrawer
          closeIcon={
            <div style={{ marginLeft: -10 }}>
              <CustomIcon icon={CloseIcon} fontSize={24} paletteColor='black' />
            </div>
          }
          onClose={handleCloseModal}
          {...otherProps}
        >
          <ResponsiveLocationDrawerContent>
            <ResponsiveListingSearchLocationSelect>
              <ListingSearchSelect
                type={type}
                label='Nereye gideceksiniz?'
                renderArrow={false}
                text={locationInput}
                icon='location-pin'
                selected={selectBox}
                onSelect={() => {
                  setSelectedBox((val) => !val);
                }}
                isInput={true}
                onInputChange={handleLocationInputChange}
                inputRef={inputRef}
                onFocus={() => setFocused(true)}
              />
            </ResponsiveListingSearchLocationSelect>

            <ResponsiveLocationItems ref={wrapperRef}>
              {showPastSearch
                ? getListItemsWrappedByHeader(
                    pastSearchesData,
                    <PastSearchesHeader onClearClick={onClearClick} />,
                    true
                  )
                : null}

              {showPopularSearch ? (
                <PopularSearches
                  items={popularSearchItems}
                  onSelectItem={(item) => {
                    autoCompleteListItemClick(item);
                    setFocused(false);
                    onPopularSearchItemSelected?.(item);
                  }}
                />
              ) : null}

              {showFilteredItems
                ? headerComponent
                  ? getListItemsWrappedByHeader(filterResult, headerComponent)
                  : getListItems(filterResult)
                : null}
            </ResponsiveLocationItems>
            <ResponsiveSearchDrawerButtonWrapper>
              <ResponsiveSearchDrawerButton
                variant='Primary'
                onClick={handleUpdateButton}
              >
                Uygula
              </ResponsiveSearchDrawerButton>
            </ResponsiveSearchDrawerButtonWrapper>
          </ResponsiveLocationDrawerContent>
        </StyledListingLocationDrawer>
      ) : null}
    </>
  );
};

export default ResponsiveListingSearchLocation;
